'use client';

import { type Dispatch, useReducer } from 'react';

import { type VideoEditSubmitAction, type VideoEditSubmitState } from '../types';

export const VIDEO_EDIT_SUBMIT_DEFAULTS: VideoEditSubmitState = {
    pendingTaskId: null,
    originalVideoUrl: '',
    originalThumbnailUrl: '',
    duration: undefined,
    aspectRatio: undefined,
    editedVideoUrl: '',
    aiContentId: '',
    lastAiContentId: '',
    aiContentType: null,
    aiModelName: null,
    status: 'idle',
    restoredFromHistory: false,
    failureReason: null,
};

export function videoEditSubmitReducer(
    state: VideoEditSubmitState,
    action: VideoEditSubmitAction,
): VideoEditSubmitState {
    switch (action.type) {
        case 'SELECTION':
            return {
                ...state,
                status: 'selection',
                editedVideoUrl: '',
                originalVideoUrl: action.originalVideoUrl,
                originalThumbnailUrl: action.originalThumbnailUrl,
                duration: action.duration,
                aspectRatio: action.aspectRatio,
                restoredFromHistory: false,
                pendingTaskId: null,
                failureReason: null,
            };
        case 'GENERATION_IN_PROGRESS':
            if (state.status === 'inProgress' && !action.pendingTaskId) return state;
            return {
                ...state,
                status: 'inProgress',
                editedVideoUrl: '',
                restoredFromHistory: false,
                pendingTaskId: action.pendingTaskId ?? null,
                aiContentId: action.aiContentId ?? state.aiContentId,
                lastAiContentId: action.lastAiContentId ?? state.lastAiContentId,
                aiContentType: action.aiContentType ?? state.aiContentType,
                aiModelName: action.aiModelName ?? state.aiModelName,
                failureReason: null,
            };
        case 'GENERATION_SUCCESS':
            return {
                ...state,
                status: 'success',
                editedVideoUrl: action.editedVideoUrl,
                aiContentId: action.aiContentId,
                lastAiContentId: action.lastAiContentId,
                aiContentType: action.aiContentType ?? state.aiContentType,
                aiModelName: action.aiModelName ?? state.aiModelName,
                restoredFromHistory: false,
                pendingTaskId: null,
                failureReason: null,
            };
        case 'GENERATION_ERROR':
            // The selected source video survives so the user can retry without re-selecting it.
            return {
                ...state,
                status: 'error',
                editedVideoUrl: '',
                restoredFromHistory: false,
                pendingTaskId: null,
                aiContentType: null,
                aiModelName: null,
                failureReason: action.failureReason ?? null,
            };
        case 'RESET':
            return VIDEO_EDIT_SUBMIT_DEFAULTS;
    }
}

export function useVideoEditSubmit(
    initialState: VideoEditSubmitState = VIDEO_EDIT_SUBMIT_DEFAULTS,
): [VideoEditSubmitState, Dispatch<VideoEditSubmitAction>] {
    return useReducer(videoEditSubmitReducer, initialState);
}
